import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { describeTestStatus } from '../helpers/testSend'
import { errorMessage } from '../services/axios/errorMessage'
import TemplateService from '../services/templateService'

// The send API only queues the message; the outcome arrives via
// /comms/template/test/status/{execution_id}. Same cadence as v1: ~1 minute.
export const POLL_INTERVAL_MS = 3000
export const MAX_POLL_ATTEMPTS = 20

/**
 * Send one test communication and follow it to its delivery outcome.
 *
 * `send(payload)` posts POST /comms/template/test (the body from
 * `buildTestPayload`), then polls the status every 3 seconds for about a
 * minute, until the API reports a terminal outcome. Used by the Test template
 * page and the pipeline wizard's test panel, so both report a test the same
 * way. A new send stops the previous one's polling; so does unmounting.
 *
 * @returns {{sendState: string, statusMessage: string, busy: boolean,
 *   send: (payload: object) => void}} `sendState` is idle, sending,
 *   pending, delivered, failed, error or timeout.
 */
const useTestSend = () => {
    const dispatch = useDispatch()
    const [sendState, setSendState] = useState('idle')
    const [statusMessage, setStatusMessage] = useState('')
    const pollRef = useRef(null)

    const stopPolling = () => {
        if (pollRef.current) clearTimeout(pollRef.current)
        pollRef.current = null
    }

    useEffect(() => stopPolling, [])

    /** Poll the delivery status until it is terminal or ~1 minute has passed. */
    const pollStatus = (executionId, attempt = 1) => {
        pollRef.current = setTimeout(async () => {
            const resp = await dispatch(
                TemplateService.getTestCommunicationStatus(executionId)
            )
            if (resp.error) {
                setSendState('error')
                setStatusMessage(
                    `Could not check the delivery status: ${errorMessage({
                        data: resp.payload,
                    })}`
                )
                return
            }
            const { state, message, accepted } = describeTestStatus(
                resp.payload?.data
            )
            if (state !== 'pending') {
                setSendState(state)
                setStatusMessage(message)
                return
            }
            if (attempt >= MAX_POLL_ATTEMPTS) {
                setSendState('timeout')
                setStatusMessage(
                    accepted
                        ? 'Accepted by the provider, but no delivery confirmation arrived within a minute. Check the inbox, or the comm logs in Reports.'
                        : 'No final delivery status after a minute. Check the comm logs in Reports before relying on this template.'
                )
                return
            }
            setStatusMessage(message)
            pollStatus(executionId, attempt + 1)
        }, POLL_INTERVAL_MS)
    }

    const send = (payload) => {
        stopPolling()
        setSendState('sending')
        setStatusMessage('Sending test communication…')
        dispatch(TemplateService.sendTestCommunication(payload)).then(
            (resp) => {
                if (resp.error) {
                    setSendState('error')
                    setStatusMessage(
                        `The test communication was not sent: ${errorMessage({
                            data: resp.payload,
                        })}`
                    )
                    return
                }
                const executionId = resp.payload?.data?.execution_id
                if (!executionId) {
                    // No id to track: say so rather than claiming delivery.
                    setSendState('timeout')
                    setStatusMessage(
                        'Queued, but the API returned no id to track delivery. Check the comm logs in Reports.'
                    )
                    return
                }
                setSendState('pending')
                setStatusMessage('Queued. Waiting for delivery confirmation…')
                pollStatus(executionId)
            }
        )
    }

    return {
        sendState,
        statusMessage,
        busy: sendState === 'sending' || sendState === 'pending',
        send,
    }
}

export default useTestSend
